import { useDebounceFn } from '@vueuse/core'

export const MIN_SUGGEST_QUERY_LENGTH = 3

export const SUGGEST_DEBOUNCE_MS = 1000

export type SuggestRefineFunction = (value: string) => void

export type UseSearchSuggestOptions = {
    onRefine?: (term: string) => void
    onBelowThreshold?: () => void
}

export type UseSearchSuggestReturn = {
    isSuggestable: (term: string) => boolean
    handleInput: (refine: SuggestRefineFunction, term: string) => void
    cancelPending: () => void
}

/**
 * The gate in front of an autocomplete's `refine()`: debounce plus minimum term length,
 * because the API records every request it receives as a search term.
 *
 * Refines trimmed, so callers comparing a result set's `query` against their input must
 * compare against the trimmed value too.
 */
export function useSearchSuggest(options: UseSearchSuggestOptions = {}): UseSearchSuggestReturn {
    const isSuggestable = (term: string): boolean => term.trim().length >= MIN_SUGGEST_QUERY_LENGTH

    const dispatch = useDebounceFn((refine: SuggestRefineFunction, term: string) => {
        refine(term)
        options.onRefine?.(term)
    }, SUGGEST_DEBOUNCE_MS)

    const handleInput = (refine: SuggestRefineFunction, term: string): void => {
        if (!isSuggestable(term)) {
            dispatch.cancel()
            options.onBelowThreshold?.()
            return
        }

        dispatch(refine, term.trim())
    }

    return {
        isSuggestable,
        handleInput,
        cancelPending: () => dispatch.cancel(),
    }
}
